import type { RemindResultDto } from '@nsb/core';
import { useState } from 'react';
import { api, errorText, queryClient } from '../api.ts';
import { askLine } from './remindWords.ts';
import type { Remind } from './useRemind.ts';
import { Button, ErrorLine, Field } from './ui.tsx';

/**
 * Remind them (design study 26, as Jason answered it on 2026-10-22): on each thing waiting on the client, where NSB
 * already sees it (the budget, a reviewer's row, a step on Decisions, a step come late or a gate marked done early, the
 * request to lock the pattern language), who was told of it and when, and a link that emails them again, at once,
 * asked first with a note if NSB likes; once a day for each thing. The one mechanism every place uses: `useRemind` reads
 * what was told (`GET /told`, one query for the page, useRemind.ts), `RemindLink` offers it, `RemindAsk` sends it.
 */

/** Remind them, or that they were today: nothing while it is asked, or when there is nothing to remind them of. */
export function RemindLink({ r, className = '' }: { r: Remind; className?: string }) {
  if (r.open || !r.state) return null;
  if (r.state === 'today') return <span className={`meta shrink-0 self-center ${className}`}>Reminded today · again tomorrow</span>;
  return (
    <button type="button" className={`link min-h-11 shrink-0 text-sm text-ink-2 hover:text-ink ${className}`} onClick={() => r.setOpen(true)}>
      Remind them
    </button>
  );
}

/** Asked first, in the page (study 26, when A): who it emails, a note if NSB likes, Send the reminder or Not now. */
export function RemindAsk({ r }: { r: Remind }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [partly, setPartly] = useState<string | null>(null);
  if (!r.open) return null;
  const close = () => {
    setNote('');
    setErr(null);
    setPartly(null);
    r.setOpen(false);
  };
  const send = async () => {
    setBusy(true);
    setErr(null);
    try {
      const res = await api<RemindResultDto>(`/projects/${r.projectId}/remind`, { body: { kind: r.kind, subjectId: r.subjectId, contactId: r.contactId, note: note.trim() || null } });
      queryClient.setQueryData(['project', r.projectId, 'told'], res.told);
      // The timeline names it, as every email the portal sends.
      await queryClient.invalidateQueries({ queryKey: ['project', r.projectId], predicate: (q) => q.queryKey[2] !== 'told' });
      if (res.failed.length) setPartly(`Reminded ${res.reminded.join(' and ')}. Not sent to ${res.failed.map((f) => `${f.name}: ${f.error}`).join('; ')}`);
      else close();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  if (partly) {
    return (
      <div className="mt-3 basis-full border-l-2 border-rule-strong pl-3 text-sm">
        <p>{partly}</p>
        <Button tone="quiet" onClick={close}>
          Close
        </Button>
      </div>
    );
  }
  return (
    <div className="mt-3 basis-full border-l-2 border-rule-strong pl-3">
      <p className="mb-3 text-sm">{askLine(r.kind, r.people)}</p>
      <Field label="A note, if you like" hint="It goes in the email above its button, in your words.">
        {(id) => <textarea id={id} rows={2} maxLength={1000} className="field mt-1 resize-y" value={note} onChange={(e) => setNote(e.target.value)} />}
      </Field>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <Button tone="secondary" busy={busy} onClick={() => void send()}>
          Send the reminder
        </Button>
        <Button tone="quiet" disabled={busy} onClick={close}>
          Not now
        </Button>
      </div>
      <ErrorLine error={err} />
    </div>
  );
}
